import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Logo } from '../components/Logo';
import { toast } from 'sonner';
import {
    Stamp, ShieldCheck, Clock, Zap, AlertTriangle, CheckCircle2, Sparkles,
    Loader2, CreditCard, FileText, UserCheck, Mail, ArrowRight, ChevronDown, Info,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const URGENZA_OPTIONS = [
    { value: 'standard', label: 'Standard', sla: '5-7 giorni lavorativi', extra: '', icon: Clock, color: 'text-[hsl(var(--muted-foreground))]' },
    { value: 'veloce',   label: 'Veloce',   sla: '2-3 giorni lavorativi', extra: '+25%', icon: Zap,   color: 'text-[hsl(35_90%_45%)]' },
    { value: 'urgente',  label: 'Urgente',  sla: '24-48 ore',             extra: '+60%', icon: AlertTriangle, color: 'text-[hsl(0_72%_52%)]' },
];

const PRICING_TIERS = [
    {
        key: 'easy',
        label: 'Standard',
        priceFrom: 149,
        color: 'border-[hsl(145_55%_70%)] bg-[hsl(145_45%_97%)]',
        badge: 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)]',
        examples: ['Tinteggiatura', 'Piccole posa piastrelle', 'Installazione condizionatori'],
        features: ['Lavori brevi (<7 gg)', '1-3 lavoratori', 'Altezza <2m', 'Rischi standard'],
    },
    {
        key: 'medium',
        label: 'Intermedio',
        priceFrom: 299,
        color: 'border-[hsl(45_100%_60%)] bg-[hsl(45_100%_97%)]',
        badge: 'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]',
        examples: ['Ristrutturazione bagno', 'Posa coperture leggere', 'Demolizioni parziali'],
        features: ['Lavori 1-4 settimane', '4-10 lavoratori', 'Altezza 2-6m', 'Verifica DPI e formazione'],
    },
    {
        key: 'complex',
        label: 'Complesso',
        priceFrom: 549,
        color: 'border-[hsl(0_72%_70%)] bg-[hsl(0_80%_97%)]',
        badge: 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)]',
        examples: ['Cantieri grandi', 'Lavori in quota >6m', 'Scavi e demolizioni'],
        features: ['Lavori >1 mese', '>10 lavoratori', 'Spazi confinati o amianto', 'Richiede PSC e CSP/CSE'],
    },
];

const STEPS = [
    { icon: FileText, title: 'Descrivi il lavoro',  body: 'Compili un form di 1 minuto: tipologia, descrizione, n. lavoratori, durata, altezza max.' },
    { icon: Sparkles, title: 'AI genera la bozza', body: 'Claude analizza il D.Lgs 81/2008, identifica rischi, DPI, formazione e calcola il prezzo in 30 secondi.' },
    { icon: CreditCard, title: 'Paghi online',    body: 'Carta, Apple Pay o Google Pay tramite Stripe. Conferma immediata via email.' },
    { icon: UserCheck, title: 'Revisione professionista', body: 'Un RSPP/CSP/CSE certificato del nostro network revisiona, integra e firma digitalmente.' },
    { icon: Mail, title: 'Ricevi il DVR firmato', body: 'PDF con timbro e firma digitale via email entro lo SLA scelto. Validità legale completa.' },
];

const FAQ = [
    { q: 'Il DVR generato è valido legalmente?', a: 'Sì. La bozza AI viene revisionata, integrata e FIRMATA DIGITALMENTE da un professionista abilitato (RSPP, CSP o CSE) del nostro network. Solo dopo la firma il documento è conforme al D.Lgs 81/2008.' },
    { q: 'Cosa succede se il mio lavoro è complesso?', a: 'L\'AI classifica automaticamente la complessità (easy / medium / complex) leggendo la descrizione e i parametri. Per cantieri >6m, lavori in quota, spazi confinati o demolizioni strutturali viene applicato il livello complex e assegnato un CSP/CSE specializzato.' },
    { q: 'Posso modificare il DVR dopo la firma?', a: 'Sì. Eventuali integrazioni richieste dal professionista durante la revisione sono già incluse nel prezzo. Modifiche successive a cantiere avviato (es. ampliamento) possono essere fatte con una nuova pratica.' },
    { q: 'Cosa è incluso nel prezzo?', a: 'Revisione completa del DVR da professionista abilitato, integrazioni tecniche e adeguamento normativo D.Lgs 81/2008, firma digitale + timbro professionale (validità legale), PDF firmato via email entro lo SLA. Sopralluogo in cantiere incluso quando necessario.' },
    { q: 'Riceverò fattura?', a: 'Sì. Fattura elettronica regolare emessa al completamento, inviata via SDI alla tua impresa. Tutti i prezzi sono mostrati IVA inclusa.' },
    { q: 'E se il pagamento non va a buon fine?', a: 'Stripe gestisce il checkout in modo sicuro. Se il pagamento fallisce non viene addebitato nulla e puoi riprovare. La bozza AI rimane salvata per 24 ore.' },
    { q: 'Posso chiedere PSC o POS oltre al DVR?', a: 'Al momento il servizio copre il DVR. PSC e POS sono in roadmap — contattaci per esigenze specifiche e ti diamo un preventivo dedicato.' },
];

export default function PublicDvr() {
    const formRef = useRef(null);
    const [step, setStep] = useState(1); // 1 = form, 2 = pricing preview, 3 = checkout loading
    const [form, setForm] = useState({
        email: '', company_name: '', piva: '', telefono: '',
        tipologia_lavoro: '', descrizione: '',
        altezza_metri: '', n_lavoratori: '', durata_giorni: '', luogo: '',
        urgenza: 'standard',
        accept_terms: false, accept_privacy: false,
    });
    const [pricing, setPricing] = useState(null);
    const [loading, setLoading] = useState(false);

    const scrollToForm = () => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const requestPricing = async (e) => {
        e?.preventDefault();
        // Validate
        if (!form.email || !form.company_name || !form.tipologia_lavoro || form.descrizione.length < 20) {
            toast.error('Compila tutti i campi obbligatori (descrizione min 20 caratteri)');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                ...form,
                altezza_metri: form.altezza_metri ? parseFloat(form.altezza_metri) : null,
                n_lavoratori: form.n_lavoratori ? parseInt(form.n_lavoratori) : null,
                durata_giorni: form.durata_giorni ? parseInt(form.durata_giorni) : null,
            };
            const r = await axios.post(`${BACKEND_URL}/api/public/dvr/pricing`, payload);
            setPricing(r.data);
            setStep(2);
            setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore generazione preventivo, riprova');
        } finally { setLoading(false); }
    };

    const goCheckout = async () => {
        if (!form.accept_terms || !form.accept_privacy) {
            toast.error('Devi accettare termini e privacy per procedere al pagamento');
            return;
        }
        setLoading(true);
        try {
            const r = await axios.post(`${BACKEND_URL}/api/public/dvr/checkout`, {
                draft_id: pricing.draft_id,
                origin_url: window.location.origin,
                accept_terms: form.accept_terms,
                accept_privacy: form.accept_privacy,
            });
            if (r.data?.url) window.location.href = r.data.url;
            else toast.error('Errore checkout, riprova');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore checkout, riprova');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="public-dvr-page">
            {/* NAV */}
            <nav className="sticky top-0 z-40 bg-[hsl(var(--background))]/95 backdrop-blur border-b border-[hsl(var(--border))]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Logo />
                    <div className="hidden md:flex items-center gap-6 text-sm font-medium">
                        <a href="#come-funziona" className="hover:text-[hsl(var(--accent))]">Come funziona</a>
                        <a href="#prezzi" className="hover:text-[hsl(var(--accent))]">Prezzi</a>
                        <a href="#faq" className="hover:text-[hsl(var(--accent))]">FAQ</a>
                        <Link to="/login" className="hover:text-[hsl(var(--accent))]" data-testid="dvr-nav-login">Login</Link>
                    </div>
                    <Button onClick={scrollToForm} className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold" data-testid="dvr-nav-cta">
                        Richiedi DVR
                    </Button>
                </div>
            </nav>

            {/* HERO */}
            <section className="bg-gradient-to-b from-[hsl(220_13%_10%)] to-[hsl(220_15%_15%)] text-white py-16 md:py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <Badge className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold mb-4">D.LGS 81/2008 · CONFORME</Badge>
                        <h1 className="font-display text-4xl md:text-6xl leading-tight mb-5">
                            DVR <span className="text-[hsl(var(--accent))]">firmato</span> da professionista abilitato in <span className="text-[hsl(var(--accent))]">24-48 ore</span>
                        </h1>
                        <p className="text-lg text-white/80 mb-8 leading-relaxed">
                            Compili 1 form, l'AI redige la bozza, un RSPP/CSP/CSE del nostro network revisiona, firma digitalmente e te lo invia via email. Validità legale completa. Da <strong className="text-[hsl(var(--accent))]">€181 IVA inclusa</strong>.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 mb-6">
                            <Button onClick={scrollToForm} className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-12 px-6 text-base gap-2" data-testid="dvr-hero-cta">
                                <Stamp className="w-5 h-5" /> Richiedi DVR ora
                            </Button>
                            <a href="#come-funziona">
                                <Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 h-12 px-6 text-base gap-2" data-testid="dvr-hero-how">
                                    Come funziona <ChevronDown className="w-4 h-4" />
                                </Button>
                            </a>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-white/70">
                            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[hsl(var(--accent))]" /> Validità legale</span>
                            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[hsl(var(--accent))]" /> Network RSPP certificati</span>
                            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-[hsl(var(--accent))]" /> Pagamento sicuro Stripe</span>
                        </div>
                    </div>
                    <div className="hidden md:block">
                        {/* Visual: stylized document with stamp */}
                        <div className="relative">
                            <div className="bg-white rounded-xl p-8 shadow-2xl rotate-2">
                                <div className="flex items-center gap-2 mb-3">
                                    <FileText className="w-5 h-5 text-[hsl(220_13%_10%)]" />
                                    <span className="font-display text-sm tracking-wider text-[hsl(220_13%_10%)]">DVR · D.LGS 81/2008</span>
                                </div>
                                <div className="space-y-2">
                                    <div className="h-3 bg-[hsl(220_13%_85%)] rounded w-3/4"></div>
                                    <div className="h-3 bg-[hsl(220_13%_85%)] rounded"></div>
                                    <div className="h-3 bg-[hsl(220_13%_85%)] rounded w-5/6"></div>
                                    <div className="h-3 bg-[hsl(220_13%_85%)] rounded w-2/3"></div>
                                </div>
                                <div className="mt-4 pt-3 border-t border-[hsl(220_13%_85%)] flex items-center justify-between">
                                    <Badge className="bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)] text-[9px]">RISCHIO ALTO</Badge>
                                    <Badge className="bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)] text-[9px]">DPI</Badge>
                                </div>
                            </div>
                            <div className="absolute -bottom-4 -right-2 w-28 h-28 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center rotate-12 shadow-2xl border-4 border-[hsl(220_13%_10%)]">
                                <div className="text-center text-[hsl(220_13%_10%)] font-display text-xs leading-tight px-2">
                                    <Stamp className="w-6 h-6 mx-auto mb-0.5" />
                                    FIRMATO<br />RSPP
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="come-funziona" className="py-16 md:py-24 bg-[hsl(var(--background))]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <Badge variant="outline" className="mb-3">PROCESSO TRASPARENTE</Badge>
                        <h2 className="font-display text-3xl md:text-5xl mb-3">Dal preventivo al DVR firmato in 5 step</h2>
                        <p className="text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">Niente burocrazia, niente attese inutili. AI per la velocità, professionista certificato per la validità legale.</p>
                    </div>
                    <div className="grid md:grid-cols-5 gap-4">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <div key={i} className="relative" data-testid={`dvr-step-${i + 1}`}>
                                    <Card className="p-5 h-full hover:shadow-lg transition-colors">
                                        <div className="w-12 h-12 rounded-full bg-[hsl(var(--accent))] flex items-center justify-center text-[hsl(220_13%_10%)] mb-3 font-display text-lg">{i + 1}</div>
                                        <Icon className="w-6 h-6 text-[hsl(var(--accent))] mb-2" />
                                        <h3 className="font-semibold mb-1.5">{s.title}</h3>
                                        <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">{s.body}</p>
                                    </Card>
                                </div>
                            );
                        })}
                    </div>
                    <div className="text-center mt-10">
                        <Link to="/dvr/come-funziona" className="text-sm font-medium text-[hsl(220_13%_10%)] hover:text-[hsl(35_90%_45%)] inline-flex items-center gap-1" data-testid="dvr-how-detail-link">
                            Guida dettagliata con screenshot <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="prezzi" className="py-16 md:py-24 bg-[hsl(var(--muted))]/30">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <Badge variant="outline" className="mb-3">PREZZI TRASPARENTI</Badge>
                        <h2 className="font-display text-3xl md:text-5xl mb-3">3 fasce in base alla complessità</h2>
                        <p className="text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">L'AI classifica automaticamente il tuo cantiere. Niente sorprese: vedi il prezzo prima di pagare.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-5">
                        {PRICING_TIERS.map((tier) => (
                            <Card key={tier.key} className={`p-6 border-2 ${tier.color}`} data-testid={`dvr-tier-${tier.key}`}>
                                <Badge className={`${tier.badge} font-bold text-xs px-3 py-1 mb-3`}>{tier.label}</Badge>
                                <div className="flex items-baseline gap-1 mb-1">
                                    <span className="text-xs text-[hsl(var(--muted-foreground))]">da</span>
                                    <span className="font-display text-4xl tabular-nums">€{tier.priceFrom}</span>
                                </div>
                                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">+ IVA 22% · €{Math.round(tier.priceFrom * 1.22).toLocaleString('it-IT')} totali</p>
                                <div className="space-y-2 mb-4">
                                    {tier.features.map((f, i) => (
                                        <div key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-[hsl(145_55%_34%)] mt-0.5 flex-shrink-0" /> {f}</div>
                                    ))}
                                </div>
                                <div className="pt-3 border-t border-[hsl(var(--border))]">
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Esempi:</div>
                                    <div className="flex flex-wrap gap-1">
                                        {tier.examples.map((e, i) => (
                                            <Badge key={i} variant="outline" className="text-xs">{e}</Badge>
                                        ))}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                    <Alert className="mt-8 max-w-3xl mx-auto bg-[hsl(45_100%_96%)] border-[hsl(45_100%_60%)]">
                        <Info className="h-4 w-4 text-[hsl(35_90%_35%)]" />
                        <AlertDescription className="text-sm">
                            <strong>Urgenza maggiorata:</strong> Standard = prezzo base. Veloce (2-3 gg) = <strong>+25%</strong>. Urgente (24-48h) = <strong>+60%</strong>.
                        </AlertDescription>
                    </Alert>
                </div>
            </section>

            {/* FORM (always rendered, scroll target) */}
            <section ref={formRef} id="richiedi" className="py-16 md:py-24 bg-[hsl(220_13%_10%)] text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8">
                        <Badge className="bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-bold mb-3">RICHIEDI DVR FIRMATO</Badge>
                        <h2 className="font-display text-3xl md:text-5xl mb-3">{step === 1 ? 'Descrivi il tuo cantiere' : 'Conferma e pagamento'}</h2>
                        <p className="text-white/70">{step === 1 ? 'L\'AI elabora la bozza in 30 secondi e ti mostra il prezzo prima del pagamento.' : 'Rivedi il preventivo dell\'AI e procedi al pagamento sicuro.'}</p>
                    </div>

                    {step === 1 && (
                        <Card className="p-6 md:p-8 bg-white text-[hsl(220_13%_10%)]">
                            <form onSubmit={requestPricing} className="space-y-5" data-testid="dvr-form-step1">
                                <div>
                                    <h3 className="font-display text-xl mb-3 flex items-center gap-2"><span className="w-7 h-7 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] flex items-center justify-center text-sm">1</span> Dati impresa</h3>
                                    <div className="grid md:grid-cols-2 gap-3">
                                        <div>
                                            <Label>Ragione sociale <span className="text-[hsl(var(--destructive))]">*</span></Label>
                                            <Input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Edilizia Rossi SRL" className="mt-1" data-testid="dvr-form-company" />
                                        </div>
                                        <div>
                                            <Label>Email <span className="text-[hsl(var(--destructive))]">*</span></Label>
                                            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="info@impresa.it" className="mt-1" data-testid="dvr-form-email" />
                                        </div>
                                        <div>
                                            <Label>P.IVA</Label>
                                            <Input value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} placeholder="12345678901" className="mt-1" data-testid="dvr-form-piva" />
                                        </div>
                                        <div>
                                            <Label>Telefono</Label>
                                            <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+39..." className="mt-1" data-testid="dvr-form-phone" />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-display text-xl mb-3 flex items-center gap-2"><span className="w-7 h-7 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] flex items-center justify-center text-sm">2</span> Tipologia di lavoro</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <Label>Tipologia <span className="text-[hsl(var(--destructive))]">*</span></Label>
                                            <Input required value={form.tipologia_lavoro} onChange={(e) => setForm({ ...form, tipologia_lavoro: e.target.value })} placeholder="Es: Ristrutturazione bagno completa" className="mt-1" data-testid="dvr-form-tipologia" />
                                        </div>
                                        <div>
                                            <Label>Descrizione dettagliata <span className="text-[hsl(var(--destructive))]">*</span></Label>
                                            <Textarea required rows={4} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} placeholder="Es: demolizione muratura non portante, rifacimento impianti idraulico e elettrico, posa nuove piastrelle e sanitari, 3 operai..." className="mt-1" data-testid="dvr-form-descrizione" />
                                            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Min 20 caratteri · più dettagli = preventivo più preciso</p>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <div>
                                                <Label className="text-xs">N. lavoratori</Label>
                                                <Input type="number" value={form.n_lavoratori} onChange={(e) => setForm({ ...form, n_lavoratori: e.target.value })} className="mt-1" data-testid="dvr-form-workers" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Durata (gg)</Label>
                                                <Input type="number" value={form.durata_giorni} onChange={(e) => setForm({ ...form, durata_giorni: e.target.value })} className="mt-1" data-testid="dvr-form-days" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Altezza max (m)</Label>
                                                <Input type="number" step="0.1" value={form.altezza_metri} onChange={(e) => setForm({ ...form, altezza_metri: e.target.value })} className="mt-1" data-testid="dvr-form-height" />
                                            </div>
                                            <div>
                                                <Label className="text-xs">Luogo</Label>
                                                <Input value={form.luogo} onChange={(e) => setForm({ ...form, luogo: e.target.value })} placeholder="Milano" className="mt-1" data-testid="dvr-form-place" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="font-display text-xl mb-3 flex items-center gap-2"><span className="w-7 h-7 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] flex items-center justify-center text-sm">3</span> Urgenza</h3>
                                    <div className="grid grid-cols-3 gap-2">
                                        {URGENZA_OPTIONS.map((u) => {
                                            const Icon = u.icon;
                                            const active = form.urgenza === u.value;
                                            return (
                                                <button
                                                    key={u.value}
                                                    type="button"
                                                    onClick={() => setForm({ ...form, urgenza: u.value })}
                                                    className={`p-3 rounded-lg border-2 text-left transition-colors ${active ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10' : 'border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]/50'}`}
                                                    data-testid={`dvr-form-urgency-${u.value}`}
                                                >
                                                    <Icon className={`w-4 h-4 mb-1 ${u.color}`} />
                                                    <div className="font-semibold text-sm flex items-center gap-1 flex-wrap">{u.label} {u.extra && <span className="text-[10px] font-bold text-[hsl(var(--accent))]">{u.extra}</span>}</div>
                                                    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{u.sla}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-12 gap-2 text-base" data-testid="dvr-form-pricing-button">
                                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> L'AI sta elaborando...</> : <><Sparkles className="w-5 h-5" /> Calcola prezzo con AI (gratis)</>}
                                </Button>

                                <p className="text-xs text-[hsl(var(--muted-foreground))] text-center">Nessun addebito in questa fase. Vedrai il prezzo prima di confermare.</p>
                            </form>
                        </Card>
                    )}

                    {step === 2 && pricing && (
                        <Card className="p-6 md:p-8 bg-white text-[hsl(220_13%_10%)]" data-testid="dvr-form-step2">
                            {/* Price block */}
                            <div className="text-center mb-6">
                                <Badge className={`${PRICING_TIERS.find((t) => t.key === pricing.complessita)?.badge} font-bold text-sm px-4 py-1 mb-3`}>
                                    Complessità · {pricing.complessita_label}
                                </Badge>
                                <div className="font-display text-5xl md:text-6xl tabular-nums mb-1">€{Number(pricing.prezzo_totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</div>
                                <div className="text-sm text-[hsl(var(--muted-foreground))]">€{pricing.prezzo_netto} imponibile + IVA 22% €{pricing.iva}</div>
                                <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
                                    <Badge variant="outline" className="gap-1"><Clock className="w-3 h-3" /> {pricing.sla}</Badge>
                                    <Badge variant="outline">Professionista: {pricing.professionista_consigliato}</Badge>
                                    {pricing.richiede_sopralluogo && <Badge className="bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)]">Sopralluogo incluso</Badge>}
                                </div>
                            </div>

                            {/* AI reasoning */}
                            <div className="bg-[hsl(var(--muted))]/30 rounded-lg p-4 mb-5 text-sm">
                                <div className="font-semibold flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" /> Valutazione AI</div>
                                <p className="text-[hsl(var(--muted-foreground))]">{pricing.motivazione_ai}</p>
                                <div className="mt-3 pt-3 border-t border-[hsl(var(--border))] flex items-center gap-3 text-xs text-[hsl(var(--muted-foreground))]">
                                    <span>{pricing.n_rischi} rischi identificati</span>
                                    {pricing.rischi_alti > 0 && <span className="text-[hsl(0_72%_52%)]">{pricing.rischi_alti} ALTO</span>}
                                </div>
                            </div>

                            {/* Preview rischi */}
                            {pricing.preview_rischi?.length > 0 && (
                                <div className="mb-5">
                                    <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">Anteprima rischi (bozza AI)</div>
                                    <div className="space-y-1.5">
                                        {pricing.preview_rischi.map((r, i) => (
                                            <div key={i} className="flex items-start gap-2 text-sm p-2 rounded bg-[hsl(var(--muted))]/30">
                                                <AlertTriangle className="w-4 h-4 text-[hsl(var(--accent))] mt-0.5 flex-shrink-0" />
                                                <span className="flex-1">{r.rischio}</span>
                                                <Badge className={r.livello === 'alto' ? 'bg-[hsl(0_80%_95%)] text-[hsl(0_72%_52%)] text-[10px]' : r.livello === 'medio' ? 'bg-[hsl(45_100%_92%)] text-[hsl(35_90%_35%)] text-[10px]' : 'bg-[hsl(145_45%_94%)] text-[hsl(145_55%_34%)] text-[10px]'}>{r.livello}</Badge>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* What's included */}
                            <div className="rounded-lg p-4 mb-5 text-sm bg-[hsl(145_45%_97%)] border border-[hsl(145_55%_70%)]">
                                <div className="font-semibold flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-[hsl(145_55%_34%)]" /> Cosa include</div>
                                <ul className="space-y-1 text-[hsl(220_13%_25%)]">
                                    <li>• Bozza AI conforme D.Lgs 81/2008</li>
                                    <li>• Revisione completa da professionista abilitato ({pricing.professionista_consigliato})</li>
                                    <li>• Firma digitale + timbro professionale</li>
                                    <li>• PDF firmato via email entro {pricing.sla}</li>
                                    {pricing.richiede_sopralluogo && <li>• Sopralluogo in cantiere (incluso)</li>}
                                </ul>
                            </div>

                            {/* Consent */}
                            <div className="space-y-2 mb-5 text-sm">
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <Checkbox checked={form.accept_terms} onCheckedChange={(v) => setForm({ ...form, accept_terms: !!v })} data-testid="dvr-consent-terms" />
                                    <span>Accetto i <a href="/terms" target="_blank" className="underline">Termini di servizio</a></span>
                                </label>
                                <label className="flex items-start gap-2 cursor-pointer">
                                    <Checkbox checked={form.accept_privacy} onCheckedChange={(v) => setForm({ ...form, accept_privacy: !!v })} data-testid="dvr-consent-privacy" />
                                    <span>Accetto l'informativa <a href="/privacy" target="_blank" className="underline">Privacy</a> e il trattamento dei dati</span>
                                </label>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button onClick={() => { setStep(1); setPricing(null); }} variant="outline" disabled={loading} data-testid="dvr-back-button">Indietro</Button>
                                <Button onClick={goCheckout} disabled={loading || !form.accept_terms || !form.accept_privacy} className="flex-1 bg-[hsl(220_13%_10%)] hover:bg-[hsl(220_13%_18%)] text-white font-bold h-12 gap-2 text-base" data-testid="dvr-checkout-button">
                                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Reindirizzamento Stripe...</> : <><CreditCard className="w-5 h-5" /> Paga €{Number(pricing.prezzo_totale).toLocaleString('it-IT', { minimumFractionDigits: 2 })} e procedi</>}
                                </Button>
                            </div>

                            <p className="text-[10px] text-[hsl(var(--muted-foreground))] text-center mt-3">Pagamento sicuro Stripe · Carta · Apple Pay · Google Pay</p>
                        </Card>
                    )}
                </div>
            </section>

            {/* TRUST */}
            <section className="py-12 bg-[hsl(var(--background))] border-y border-[hsl(var(--border))]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6 text-center">
                    <div data-testid="dvr-trust-1">
                        <ShieldCheck className="w-10 h-10 mx-auto mb-2 text-[hsl(var(--accent))]" />
                        <div className="font-semibold">D.Lgs 81/2008</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">Documento giuridicamente valido firmato da professionista abilitato</div>
                    </div>
                    <div data-testid="dvr-trust-2">
                        <UserCheck className="w-10 h-10 mx-auto mb-2 text-[hsl(var(--accent))]" />
                        <div className="font-semibold">Network certificato</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">RSPP, CSP, CSE e ingegneri iscritti agli ordini professionali</div>
                    </div>
                    <div data-testid="dvr-trust-3">
                        <CreditCard className="w-10 h-10 mx-auto mb-2 text-[hsl(var(--accent))]" />
                        <div className="font-semibold">Pagamento sicuro</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">Stripe 3D-Secure · No carte salvate da noi</div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="py-16 md:py-24 bg-[hsl(var(--muted))]/30">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <Badge variant="outline" className="mb-3">DOMANDE FREQUENTI</Badge>
                        <h2 className="font-display text-3xl md:text-5xl">FAQ</h2>
                    </div>
                    <Accordion type="single" collapsible className="space-y-2">
                        {FAQ.map((f, i) => (
                            <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-lg border px-4" data-testid={`dvr-faq-${i}`}>
                                <AccordionTrigger className="text-left hover:no-underline">{f.q}</AccordionTrigger>
                                <AccordionContent className="text-sm text-[hsl(var(--muted-foreground))]">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-16 bg-[hsl(220_13%_10%)] text-white text-center">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Stamp className="w-12 h-12 mx-auto mb-4 text-[hsl(var(--accent))]" />
                    <h2 className="font-display text-3xl md:text-5xl mb-4">Pronto a metterti in regola in 48h?</h2>
                    <p className="text-white/70 mb-8">Compila il form, l'AI elabora la bozza in 30 secondi, vedi il prezzo prima di pagare.</p>
                    <Button onClick={scrollToForm} className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-12 px-8 text-base gap-2" data-testid="dvr-final-cta">
                        <Stamp className="w-5 h-5" /> Richiedi DVR ora
                    </Button>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-[hsl(220_13%_8%)] text-white/60 py-8 text-center text-xs">
                <div className="max-w-6xl mx-auto px-4">
                    <Logo />
                    <p className="mt-3">© {new Date().getFullYear()} CantiereAI · Servizio di firma DVR professionale conforme D.Lgs 81/2008</p>
                    <div className="mt-2 flex justify-center gap-4">
                        <a href="/privacy" className="hover:text-white">Privacy</a>
                        <a href="/terms" className="hover:text-white">Termini</a>
                        <Link to="/login" className="hover:text-white">Login</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
